<template>
	<div v-editable="blok">
		<PageHeaderGeneral :blok="blok" :image="articleHeroImage" hide-title>
			<template #aside>
				<div class="header-tag-list">
					<span
						v-for="(tag, index) in tags"
						:key="index"
						class="gray-pill"
					>
						{{ tag.name }}
					</span>
				</div>
			</template>
		</PageHeaderGeneral>

		<main class="container py-10 mt-12 md:flex md:gap-14 lg:gap-20">
			<div class="mb-6 md:basis-1/2 lg:basis-[auto] lg:mb-0 lg:flex-1">
				<div class="flex items-center gap-3 mb-7">
					<span class="text-body-tertiary typo-body-l">{{ dateOfPublication }}</span>

					<AuthorBadge v-if="blok.author" :blok="blok.author" is-dark />
				</div>

				<h1 class="typo-display mb-4 md:mb-6">
					{{ blok.title }}
				</h1>

				<NuxtPicture
					v-if="blok.image?.filename"
					provider="storyblok"
					:modifiers="blok.image.focus ? {filters: {focal: blok.image.focus}} : {}"
					:src="blok.image.filename"
					:alt="blok.image.alt || ''"
					class="block mb-4 md:mb-6"
				/>

				<p class="typo-body-xl mb-6 md:mb-8" v-text="blok.perex" />

				<h2 class="text-base font-semibold font-secondary mb-2">
					Náročnost receptu
				</h2>
				<div class="flex gap-4 lg:gap-[5rem] text-body-primary typo-body-m">
					<div class="flex items-center gap-2">
						<NuxtIcon name="clock" filled class="text-base text-icon-primary" />
						Příprava {{ preparationTime?.name }}
					</div>
					<DifficultyBadge :value="String(difficulty)" :difficulty="Number(blok.difficulty)" />
				</div>
			</div>

			<div v-if="blok.ingredients" class="md:basis-1/2 lg:basis-[auto] lg:shrink-0 lg:w-[450px]">
				<div class="p-6 bg-background-secondary shadow-sm rounded-xl">
					<h2 class="typo-h4 mb-4">
						Co budete potřebovat
					</h2>
					<StoryblokComponent
						v-for="item in blok.ingredients"
						:key="item._uid"
						:blok="item"
					/>
				</div>
			</div>
		</main>

		<div class="space-y-section">
			<section v-if="blok.listOfSteps" class="my-10 bg-background-secondary py-4 md:py-8">
				<div class="container">
					<h2
						class="
							typo-h4
							mb-4
							md:mb-8
						"
					>
						Postup
					</h2>

					<StoryblokComponent
						v-for="(item, index) in blok.listOfSteps"
						:key="item._uid"
						:blok="item"
						:index="index"
					/>
				</div>
			</section>

			<div class="container">
				<SocialShare :title="blok.title" />
			</div>

			<StoryblokComponent
				v-for="item in blok.discountProducts"
				:key="item._uid"
				:blok="item"
			/>

			<StoryblokComponent
				v-for="item in blok.relatedArticles"
				:key="item._uid"
				:blok="item"
			/>

			<StoryblokComponent
				v-if="blok.newsletterSignupBanner && blok.newsletterSignupBanner.content"
				:id="blok.newsletterSignupBanner.slug"
				:blok="blok.newsletterSignupBanner.content"
			/>

			<StoryblokComponent
				v-if="followUpTiles"
				:blok="followUpTiles"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {format} from 'date-fns';
	import {useStoryblokDatasourcesStore} from '@/stores/storyblokDatasources';
	import articleHeroImages from '~/resources/articleHeroImages';
	import type {PageTypeRecipesRecipeDetailStoryblok} from '@/storyblok/types';
	import type {AuthorBadgeStoryBlok} from '@/types/authorBadge';
	import {FOLLOW_UP_TILES_SLUG} from '#shared/constants';

	interface Props {
		blok: PageTypeRecipesRecipeDetailStoryblok | AuthorBadgeStoryBlok
	}

	const props = defineProps<Props>();

	const articleHeroImage = inject('articleHeroImage', articleHeroImages.recipe);

	const store = useStoryblokDatasourcesStore();
	const dateOfPublication = ref(format(new Date(props.blok.dateOfPublication), 'dd. MM. yyyy'));

	await store.fetchEntries('difficulty', 'preparationTime');
	const tags = computed(() => store.tags?.filter((item) => props.blok.listOfTags.includes(item.value)));
	const difficulty = computed(() => store.difficulty?.filter((entry) => props.blok.difficulty?.includes(entry.value)).map((entry) => entry.name).join(', '));
	const preparationTime = computed(() => {
		const filteredEntries = store.preparationTime?.filter((entry) => entry.value === props.blok.preparationTime);
		return filteredEntries ? filteredEntries.shift() : null;
	});

	const {data: fetchedFollowUpTiles} = await useSingleStoryFetch(
		FOLLOW_UP_TILES_SLUG,
		{},
		{
			lazy: true,
			server: true,
			immediate: !props.blok.followUpTiles?.content,
		},
	);

	const followUpTiles = computed(() => {
		if (props.blok.followUpTiles?.content) {
			return props.blok.followUpTiles.content;
		}
		return fetchedFollowUpTiles.value?.content;
	});
</script>
